import React from 'react';
import { motion } from 'framer-motion';

const Logo = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-14 h-14', 
    lg: 'w-20 h-20'
  };

  return (
    <motion.div
      animate={{ 
        rotate: [0, 360],
        scale: [1, 1.05, 1]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={`${sizeClasses[size]} ${className} flex items-center justify-center relative`}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <linearGradient id={`worldGrad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{stopColor:'#4f46e5', stopOpacity:1}} />
            <stop offset="50%" style={{stopColor:'#7c3aed', stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:'#ec4899', stopOpacity:1}} />
          </linearGradient>
          <linearGradient id={`coreGrad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{stopColor:'#fbbf24', stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:'#f59e0b', stopOpacity:1}} />
          </linearGradient>
          <filter id={`glow-${size}`}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* World sphere */}
        <motion.circle 
          cx="50" 
          cy="50" 
          r="42" 
          fill={`url(#worldGrad-${size})`} 
          filter={`url(#glow-${size})`}
          stroke="rgba(255,255,255,0.3)" 
          strokeWidth="1"
          animate={{ 
            r: [40, 44, 40],
            opacity: [0.9, 1, 0.9]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Continents/landmasses */}
        <motion.path 
          d="M20,35 Q30,25 45,30 Q55,35 65,25 Q75,30 80,40 Q70,50 60,45 Q45,50 35,55 Q25,45 20,35Z" 
          fill="rgba(255,255,255,0.2)"
          animate={{
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />
        <motion.path 
          d="M15,60 Q25,55 35,65 Q45,70 40,75 Q30,80 20,75 Q10,70 15,60Z" 
          fill="rgba(255,255,255,0.15)"
          animate={{
            opacity: [0.15, 0.3, 0.15]
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.path 
          d="M60,60 Q70,55 80,65 Q85,70 80,80 Q70,85 65,75 Q55,70 60,60Z" 
          fill="rgba(255,255,255,0.15)"
          animate={{
            opacity: [0.15, 0.3, 0.15]
          }}
          transition={{
            duration: 3.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        />
        
        {/* Grid lines */}
        <motion.circle 
          cx="50" 
          cy="50" 
          r="42" 
          fill="none" 
          stroke="rgba(255,255,255,0.2)" 
          strokeWidth="0.5"
          animate={{
            strokeOpacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.ellipse 
          cx="50" 
          cy="50" 
          rx="42" 
          ry="21" 
          fill="none" 
          stroke="rgba(255,255,255,0.2)" 
          strokeWidth="0.5"
          animate={{
            strokeOpacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />
        <motion.ellipse 
          cx="50" 
          cy="50" 
          rx="21" 
          ry="42" 
          fill="none" 
          stroke="rgba(255,255,255,0.2)" 
          strokeWidth="0.5"
          animate={{
            strokeOpacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 2.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Central core with language symbols */}
        <motion.circle 
          cx="50" 
          cy="50" 
          r="18" 
          fill={`url(#coreGrad-${size})`} 
          opacity="0.9"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.9, 1, 0.9]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.circle 
          cx="50" 
          cy="50" 
          r="18" 
          fill="none" 
          stroke="rgba(255,255,255,0.4)" 
          strokeWidth="1"
          animate={{
            r: [16, 20, 16]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />
        
        {/* Language symbols */}
        <motion.text 
          x="42" 
          y="45" 
          fontSize="8" 
          fontWeight="bold" 
          fill="white"
          animate={{
            opacity: [0.8, 1, 0.8],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          A
        </motion.text>
        <motion.text 
          x="55" 
          y="45" 
          fontSize="8" 
          fontWeight="bold" 
          fill="white"
          animate={{
            opacity: [0.8, 1, 0.8],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        >
          文
        </motion.text>
        <motion.text 
          x="42" 
          y="58" 
          fontSize="8" 
          fontWeight="bold" 
          fill="white"
          animate={{
            opacity: [0.8, 1, 0.8],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          Ñ
        </motion.text>
        <motion.text 
          x="55" 
          y="58" 
          fontSize="8" 
          fontWeight="bold" 
          fill="white"
          animate={{
            opacity: [0.8, 1, 0.8],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        >
          ع
        </motion.text>
        
        {/* Central connector */}
        <motion.circle 
          cx="50" 
          cy="50" 
          r="3" 
          fill="white" 
          opacity="0.8"
          animate={{
            r: [2, 4, 2],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </svg>
    </motion.div>
  );
};

export default Logo;